import { ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Accordion, AccordionSummary, Box, Typography, AccordionDetails } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineKeyboardArrowLeft, MdCircle, MdOutlineCircle } from 'react-icons/md';
import { useNavigate, NavLink } from 'react-router-dom';
import { IMenu } from '../../interfaces/menu';
import { useSidebar } from '../../contexts/SidebarContext';
import getMainDomain from '../../utils/getMainDomain.ts';

interface IProps {
	items: IMenu[];
	activeItem: string;
	activeSubItem: string;
	handleSubItemSelect: (subTitle: string, parentTitle: string) => void;
}
export default function SidebarItem({ items, activeItem, activeSubItem, handleSubItemSelect }: IProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { sidebarOpen } = useSidebar();

	function logOut() {
		localStorage.removeItem('token');
		document.cookie = `token=;domain=.${getMainDomain()}; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
		setTimeout(() => {
			navigate('/auth/login');
		}, 500);
	}

	return items.map((item, index) => {
		const isActive = activeItem === item.itemTitle;
		const isLogoutItem = item.itemTitle === 'exit';

		const onClickFunction = isLogoutItem ? () => logOut() : () => handleSubItemSelect('', item.itemTitle);

		if (item.route && item.hideArrow) {
			return (
				<React.Fragment key={index}>
					<NavLink to={item.route} onClick={() => onClickFunction()}>
						<ListItem disablePadding className={`${isActive && 'rounded-md bg-primary-focus'} ${!open && 'flex h-10 items-center justify-center ps-2'}`}>
							<ListItemButton className="min-h-12 justify-center">
								<ListItemIcon className="me-2 min-w-0 justify-center">{item.icon}</ListItemIcon>
								{sidebarOpen && <ListItemText primary={t(item.itemTitle)} className={`text-text-secondary ${isActive && 'text-text-primary'}`} />}
							</ListItemButton>
						</ListItem>
					</NavLink>
					{index < items.length - 1 && <Divider />}
				</React.Fragment>
			);
		} else {
			return (
				<React.Fragment key={index}>
					<Accordion
						disableGutters
						className={`${isActive && '[&>*]:rounded-md [&>*]:bg-primary-focus'} ${!open && 'flex h-10 items-center justify-center ps-2'}`}
						elevation={0}
						sx={{
							'&:before': {
								display: 'none',
							},
						}}
						onChange={() => {
							handleSubItemSelect('', item.itemTitle);
						}}
					>
						<AccordionSummary expandIcon={sidebarOpen ? <MdOutlineKeyboardArrowLeft className="text-text-secondary" /> : null} id={`panel${index}-header`}>
							<Box component={'div'} className="flex items-center text-sm text-text-secondary">
								<ListItemIcon className="me-2 min-w-0">{item.icon}</ListItemIcon>
								{sidebarOpen && <Typography className={`text-text-secondary ${isActive && 'text-text-primary'}`}>{t(item.itemTitle)}</Typography>}
							</Box>
						</AccordionSummary>
						{sidebarOpen &&
							item.subMenuItems?.map((subItem, i) => {
								const isSelected = activeSubItem === subItem.title;

								return (
									<NavLink to={subItem.route} key={i} onClick={() => handleSubItemSelect(subItem.title, item.itemTitle)} className={'flex items-center bg-background-paper ps-6'}>
										{isSelected ? <MdCircle className="h-2 w-2 text-primary-main" /> : <MdOutlineCircle className="h-2 w-2" />}
										<AccordionDetails className={`my-2 ms-2 flex h-9 grow items-center bg-white py-0 text-sm text-text-secondary ${isSelected && 'rounded-md bg-gradient-to-r from-primary-light to-primary-main text-white'}`}>
											<Typography>{t(subItem.title)}</Typography>
										</AccordionDetails>
									</NavLink>
								);
							})}
					</Accordion>
				</React.Fragment>
			);
		}
	});
}
